/**
 * Idle sign-out timing (D2-SEC-014, #4081).
 *
 * The last-activity time is shared across every open CRM tab through
 * localStorage, so working in one tab keeps the others signed in, and a
 * "Stay signed in" in one tab clears the prompt in all of them. The agency's
 * timeout comes from the server (`idle_timeout_minutes` on the login / me
 * payload; 0 = off).
 */

export const LAST_ACTIVITY_STORAGE_KEY = "lwp_last_activity";
/** sessionStorage flag the login page reads to explain an idle sign-out. */
export const IDLE_SIGNED_OUT_STORAGE_KEY = "lwp_idle_signed_out";
/** How long before sign-out the "Still signed in?" prompt appears. */
export const IDLE_PROMPT_SECONDS = 60;
/** Activity is written to storage at most this often (ms). */
export const ACTIVITY_WRITE_THROTTLE_MS = 5000;

// No "scroll": it also fires for programmatic scrolling and scroll anchoring,
// which would keep an unattended page alive. Real scrolling arrives as
// wheel / touch / key / pointer input.
export const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "touchstart",
] as const;

export function readLastActivity(): number | null {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
    const value = raw ? Number(raw) : NaN;
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

export function writeLastActivity(at: number): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(at));
  } catch {
    // Storage unavailable (private mode quota): this tab still keeps its own clock.
  }
}

export type IdleState =
  | { phase: "active" }
  | { phase: "prompt"; secondsLeft: number }
  | { phase: "expired" };

/** Where a session stands, given its last activity and the timeout. */
export function idleState(
  lastActivity: number,
  now: number,
  timeoutMinutes: number,
): IdleState {
  const remainingMs = lastActivity + timeoutMinutes * 60_000 - now;
  if (remainingMs <= 0) return { phase: "expired" };
  if (remainingMs <= IDLE_PROMPT_SECONDS * 1000) {
    return { phase: "prompt", secondsLeft: Math.ceil(remainingMs / 1000) };
  }
  return { phase: "active" };
}
